import { postmortemApiEndpoint } from "../config";
import { fetchToken } from "./fetchTokenService";
import { loginRequest } from "../auth/authConfig";
import { getConsumerKey } from "./maerskApiTokenService";
import { cachedRequest } from "../Utils/cachedRequest";

// Helper function for retried fetch requests
const fetchWithRetryHelper = async (url, options = {}, retries = 3, delay = 1000) => {
  try {
    const response = await fetch(url, options);
    if (!response.ok && response.status >= 500) {
      throw new Error(`Server error: ${response.status}`);
    }
    return response;
  } catch (error) {
    const isRetryable = !error.message || error.message.includes('Server error') || error instanceof TypeError;
    if (retries > 0 && isRetryable) {
      await new Promise((res) => setTimeout(res, delay));
      return fetchWithRetryHelper(url, options, retries - 1, delay * 2);
    }
    throw error;
  }
};

/**
 * Service for managing Chained Five Whys in PostMortemEnhancements collection
 * Uses separate collection to maintain backward compatibility with existing PostMortem data
 */
export const ChainedFiveWhysService = () => {
  
  /**
   * Fetch chained five whys for an incident
   * @param {string} incidentNumber - The incident number
   * @param {object} instance - MSAL instance for authentication
   * @param {array} accounts - MSAL accounts
   * @param {object} options - throwOnError: rethrow failures instead of returning []
   * @returns {Promise<array>} Array of chains or empty array
   */
  const getChainedFiveWhys = async (incidentNumber, instance, accounts, { throwOnError = false } = {}) => {
    try {
      console.log('[ChainedFiveWhysService] Fetching chains for incident:', incidentNumber);
      
      const tokenResponse = await fetchToken(instance, loginRequest, accounts);

      const response = await fetchWithRetryHelper(
        `${postmortemApiEndpoint}/enhancements/${encodeURIComponent(incidentNumber)}/chained-five-whys`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${tokenResponse.idToken}`,
            'Consumer-Key': getConsumerKey(),
            'Content-Type': 'application/json'
          }
        },
        3,
        1000
      );

      if (response.status === 404) {
        // No enhancements exist yet - return empty array
        console.log('[ChainedFiveWhysService] No chains found (404), returning empty array');
        return [];
      }

      if (!response.ok) {
        console.error('[ChainedFiveWhysService] Error fetching chains:', response.status);
        throw new Error(`Failed to fetch chained five whys: ${response.status}`);
      }

      const data = await response.json();
      console.log('[ChainedFiveWhysService] Fetched chains:', data?.chains?.length || 0);
      return data?.chains || [];
      
    } catch (error) {
      console.error('[ChainedFiveWhysService] Error in getChainedFiveWhys:', error);
      if (throwOnError) throw error;
      // Return empty array on error to allow fallback to old format
      return [];
    }
  };

  /**
   * Read chains through the shared React Query cache.
   * Failures are rethrown so an error is never cached as "no chains".
   * @param {object} queryClient - React Query client
   * @param {boolean} fresh - bypass the cache and fetch now (e.g. before validation on submit)
   */
  const getChainedFiveWhysCached = (queryClient, incidentNumber, instance, accounts, { fresh = false } = {}) =>
    cachedRequest(
      queryClient,
      ["chainedFiveWhys", incidentNumber],
      () => getChainedFiveWhys(incidentNumber, instance, accounts, { throwOnError: true }),
      fresh ? { staleTime: 0 } : {}
    );

  /**
   * Save/Update chained five whys for an incident
   * @param {string} incidentNumber - The incident number
   * @param {array} chains - Array of chain objects
   * @param {object} instance - MSAL instance
   * @param {array} accounts - MSAL accounts
   * @returns {Promise<object>} Response data
   */
  const saveChainedFiveWhys = async (incidentNumber, chains, instance, accounts) => {
    try {
      console.log('[ChainedFiveWhysService] Saving chains for incident:', incidentNumber, 'Count:', chains.length);
      
      const tokenResponse = await fetchToken(instance, loginRequest, accounts);

      const response = await fetchWithRetryHelper(
        `${postmortemApiEndpoint}/enhancements/${encodeURIComponent(incidentNumber)}/chained-five-whys`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokenResponse.idToken}`,
            'Consumer-Key': getConsumerKey(),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ chainedFiveWhys: chains })
        },
        3,
        1000
      );

      if (!response.ok) {
        console.error('[ChainedFiveWhysService] Error saving chains:', response.status);
        throw new Error(`Failed to save chained five whys: ${response.status}`);
      }

      const data = await response.json();
      console.log('[ChainedFiveWhysService] Chains saved successfully');
      return data;
      
    } catch (error) {
      console.error('[ChainedFiveWhysService] Error in saveChainedFiveWhys:', error);
      throw error;
    }
  };

  /**
   * Migrate old questionarie format to chained format
   * Creates a single "Chain A" with the old questions
   * @param {array} oldQuestions - Old questionarie array
   * @param {string} description - RCI description
   * @returns {array} Array with single chain
   */
  const migrateOldFormat = (oldQuestions, description) => {
    console.log('[ChainedFiveWhysService] Migrating old format to Chain A');
    
    // Filter out empty questions
    const validQuestions = oldQuestions.filter(q => q.question?.trim() || q.answer?.trim());
    
    if (validQuestions.length === 0) {
      // No valid questions, return empty array
      return [];
    }

    // Create Chain A with old data
    const chainA = {
      chainId: `chain-${Date.now()}`,
      chainLetter: 'A',
      chainLabel: 'Root Cause Analysis', // Default label
      chainDescription: description || '',
      whys: validQuestions.map((q, index) => ({
        whyNumber: index + 1,
        question: q.question || '',
        answer: q.answer || ''
      }))
    };

    return [chainA];
  };

  /**
   * Generate next chain letter (A, B, C, ...)
   * @param {array} existingChains - Current chains
   * @returns {string} Next letter
   */
  const getNextChainLetter = (existingChains) => {
    if (!existingChains || existingChains.length === 0) {
      return 'A';
    }
    
    const lastLetter = existingChains[existingChains.length - 1].chainLetter;
    return String.fromCharCode(lastLetter.charCodeAt(0) + 1);
  };

  /**
   * Create empty chain with default structure
   * Starts with 1 why, user can add more (minimum 5 for complete chain)
   * @param {string} chainLetter - Letter for the chain (A, B, C...)
   * @returns {object} New chain object
   */
  const createEmptyChain = (chainLetter) => {
    return {
      chainId: `chain-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      chainLetter: chainLetter,
      chainLabel: '', // User will fill this
      chainDescription: '', // Optional description
      whys: [
        {
          whyNumber: 1,
          question: '',
          answer: ''
        }
      ]
    };
  };

  return {
    getChainedFiveWhys,
    getChainedFiveWhysCached,
    saveChainedFiveWhys,
    migrateOldFormat,
    getNextChainLetter,
    createEmptyChain
  };
};
